import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { API_BASE_URL, fetchJson } from './api';
import { buildYearlyInvoiceRows, computeProgrammeDurationMonths } from './financialSchedule';

export default function InvoiceLineChart() {
    const [data, setData] = useState([]);
    const [targetGbp, setTargetGbp] = useState(40000);
    const [isNarrow, setIsNarrow] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
        return window.matchMedia('(max-width: 600px)').matches;
    });

    const chartMargin = isNarrow
        ? { top: 6, right: 6, left: 0, bottom: 0 }
        : { top: 6, right: 30, left: 0, bottom: 4 };

    const yAxisWidth = isNarrow ? 44 : 60;
    const chartHeight = isNarrow ? 240 : 292;

    function authHeaderMaybe() {
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (!token) return {};
            return { Authorization: `Bearer ${token}` };
        } catch {
            return {};
        }
    }

    function formatShortGbp(value) {
        const v = Number(value);
        if (!Number.isFinite(v)) return '£0';
        if (v >= 1000000) return '£' + (v / 1000000).toFixed(1) + 'M';
        if (v >= 1000) return '£' + (v / 1000).toFixed(0) + 'K';
        return '£' + Math.round(v);
    }

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mql = window.matchMedia('(max-width: 600px)');
        const onChange = () => setIsNarrow(!!mql.matches);
        try {
            mql.addEventListener('change', onChange);
        } catch {
            mql.addListener(onChange);
        }
        onChange();
        return () => {
            try {
                mql.removeEventListener('change', onChange);
            } catch {
                mql.removeListener(onChange);
            }
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function loadChartData() {
            try {
                // Keep target baseline in sync with admin settings.
                try {
                    const financeJson = await fetchJson(`${API_BASE_URL}/api/settings/finance`, {
                        method: 'GET',
                        headers: authHeaderMaybe()
                    });
                    const rawTarget = financeJson?.finance?.invoice_revenue_target_gbp;
                    const n = Number(rawTarget);
                    if (Number.isFinite(n) && n >= 0) setTargetGbp(Math.round(n));
                } catch {
                    // ignore: fall back to default
                }

                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const currentYear = new Date().getFullYear();
                const monthlyTotals = new Array(12).fill(0);

                const raw = await fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET', headers: authHeaderMaybe() });
                const baseProjects = Array.isArray(raw) ? raw : [];

                // Map backend fields into schedule fields
                const projects = baseProjects.map((p) => ({
                    reference: p.reference ?? '',
                    instructionDate: p.instruction_date ?? null,
                    initialReportFee: p.initial_report_fee ?? null,
                    interimReportFee: p.interim_report_fee ?? null,
                    // Defaults; will be overridden by questionnaire output when available
                    startOnSiteDate: p.works_start_month ?? null,
                    durationMonths: p.project_duration ?? null
                })).filter((p) => String(p.reference || '').trim() !== '');

                // Enrich start date + duration from questionnaire output (Programme table)
                const enriched = await Promise.all(projects.map(async (p) => {
                    const ref = String(p.reference || '').trim();
                    try {
                        const irUrl = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/initial-report?mode=surveyor`;
                        const ir = await fetchJson(irUrl, { method: 'GET', headers: authHeaderMaybe() });
                        const programmeRow = ir?.tables?.ini_section_8_programme?.[0] || null;
                        if (!programmeRow) return p;

                        return {
                            ...p,
                            startOnSiteDate: programmeRow?.start_date || p.startOnSiteDate,
                            durationMonths: (computeProgrammeDurationMonths(programmeRow) ?? p.durationMonths)
                        };
                    } catch (e) {
                        return p;
                    }
                }));

                enriched.forEach((p) => {
                    const rows = buildYearlyInvoiceRows(p, currentYear);
                    rows.combined.forEach((value, monthIdx) => {
                        monthlyTotals[monthIdx] += Number(value || 0);
                    });
                });

                const chartData = months.map((month, index) => ({ month, total: monthlyTotals[index] }));
                if (!cancelled) setData(chartData);
            } catch (e) {
                console.error('Error loading chart data:', e);
                if (!cancelled) setData([]);
            }
        }

        loadChartData();
        // Light refresh in case backend values change while the dashboard is open.
        const interval = setInterval(loadChartData, 60_000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, []);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                borderRadius: 22,
                overflow: 'hidden',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.52), rgba(245,248,252,0.74))',
                border: '1px solid rgba(255,255,255,0.58)',
                padding: isNarrow ? 8 : 12
            }}
        >
            {data.length === 0 ? (
                <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                    No invoice data available
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <LineChart data={data} margin={chartMargin}>
                        <CartesianGrid strokeDasharray="3 4" vertical={!isNarrow} stroke="rgba(148, 163, 184, 0.28)" />
                        <XAxis
                            dataKey="month"
                            interval={isNarrow ? 0 : 'preserveEnd'}
                            minTickGap={isNarrow ? 0 : 5}
                            tick={{ fontSize: isNarrow ? 9 : 12, fill: '#64748b' }}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={isNarrow ? 4 : 6}
                        />
                        <YAxis
                            domain={[0, Math.max(60000, targetGbp || 0, ...data.map((d) => Number(d?.total) || 0))]}
                            tick={{ fontSize: isNarrow ? 11 : 12, fill: '#64748b' }}
                            width={yAxisWidth}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={6}
                            tickFormatter={(value) => {
                                return formatShortGbp(value);
                            }}
                        />
                        <Tooltip
                            contentStyle={{
                                borderRadius: 14,
                                border: '1px solid rgba(255,255,255,0.72)',
                                background: 'rgba(255,255,255,0.96)',
                                boxShadow: '0 18px 34px rgba(15, 23, 42, 0.12)'
                            }}
                            formatter={(value) => {
                                return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
                            }}
                        />
                        {isNarrow ? null : <Legend />}
                        <ReferenceLine
                            y={targetGbp}
                            stroke="#e11d48"
                            strokeDasharray="6 2"
                            label={{
                                value: `Target ${formatShortGbp(targetGbp)}`,
                                position: 'insideTopRight',
                                fill: '#e11d48',
                                fontWeight: 600,
                                fontSize: isNarrow ? 11 : 12
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#2563eb"
                            strokeWidth={2}
                            dot={{ fill: '#2563eb', r: isNarrow ? 3 : 4 }}
                            activeDot={{ r: isNarrow ? 5 : 6 }}
                            name="Total Invoices"
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}
